import { Money, moneyMultiply } from "@redotech/money";
import { moneyFormat } from "@redotech/money/util";
import { useLoad } from "@redotech/react-util/load";
import { CheckoutExperienceStep } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { useTranslation } from "@redotech/redo-i18n";
import CodeBrowserIcon from "@redotech/redo-icons/code-browser.svg";
import CursorClickIcon from "@redotech/redo-icons/cursor-click-01.svg";
import ShoppingCartIcon from "@redotech/redo-icons/shopping-cart-03.svg";
import TruckIcon from "@redotech/redo-icons/truck-01.svg";
import { RedoConversionRpcClientContext } from "@redotech/redo-merchant-app-common/redo-conversion-rpc-client-provider";
import {
  RedoBadge,
  RedoBadgeColor,
} from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Tooltip } from "@redotech/redo-web/arbiter-components/tooltip/tooltip";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Handle, Position } from "@xyflow/react";
import { memo, ReactNode, useContext, useMemo } from "react";
import { CheckoutTreeAnalyticsContext } from "../../../../conversion/context/checkout-tree-analytics-provider";
import { PartialWithRequiredType } from "../steps";
import { BaseNode } from "./base-node";
import * as checkoutCss from "./checkout-experience-node.module.css";
import { CheckoutExperiencePpuHover } from "./checkout-experience-ppu-hover";
import { CheckoutExperienceShippingHover } from "./checkout-experience-shipping-hover";
import { CheckoutExperienceWidgetsHover } from "./checkout-experience-widgets-hover";
import * as css from "./node.module.css";
import { selectWidgetsForExperiencePreview } from "./select-widgets-for-experience-preview";

type IconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;

export interface CheckoutExperienceNodeData {
  step: PartialWithRequiredType<CheckoutExperienceStep>;
  deleteElements: (id: string) => void;
  readonly: boolean;
}

export const CheckoutExperienceNode = memo(function CheckoutExperienceNode({
  id,
  data,
  selected,
}: {
  id: string;
  data: CheckoutExperienceNodeData;
  selected?: boolean | undefined;
}) {
  const { t } = useTranslation();
  const { metrics } = useMetrics({ nodeId: id });

  return (
    <Flex dir="column" gap="md">
      <BaseNode
        containerClassName={css.checkoutNode}
        icon={
          <RedoFeaturedIcon color="gray" Icon={ShoppingCartIcon} size="sm" />
        }
        onDelete={data.readonly ? undefined : () => data.deleteElements(id)}
        selected={selected}
        step={data.step}
        title={t("Checkout experience")}
        wrapperClassName={selected ? undefined : checkoutCss.interactiveWrapper}
      >
        <Handle id="top" position={Position.Top} type="target" />
        {data.step.name != null && (
          <Flex align="flex-start" dir="column" gap="xs">
            <Text fontSize="xxs" textColor="secondary">
              {t("Name")}
            </Text>
            <Flex w="full">
              <RedoBadge color="gray" size="xs" text={data.step.name} />
            </Flex>
          </Flex>
        )}
        {data.step.checkoutExperienceId != null && (
          <CheckoutExperienceSummary
            checkoutExperienceId={data.step.checkoutExperienceId}
          />
        )}
      </BaseNode>
      {data.readonly && metrics.status === "display-metrics" && (
        <MetricsCallout metrics={metrics.value} />
      )}
    </Flex>
  );
});

/** Shows category chips and fetched checkout-experience details. */
interface SummaryChip {
  key: string;
  Icon: IconComponent;
  count: number;
  label: string;
  color: RedoBadgeColor;
  hover: ReactNode;
}

const CheckoutExperienceSummary = memo(function CheckoutExperienceSummary({
  checkoutExperienceId,
}: {
  checkoutExperienceId: string;
}) {
  const { t } = useTranslation();
  const conversionRpcClient = useContext(RedoConversionRpcClientContext);

  const experienceLoad = useLoad(
    async (signal) => {
      if (!conversionRpcClient) {
        return undefined;
      }
      return conversionRpcClient.getCheckoutExperienceById(
        { checkoutExperienceId },
        { signal },
      );
    },
    [conversionRpcClient, checkoutExperienceId],
  );

  const widgetsLoad = useLoad(
    async (signal) => {
      if (!conversionRpcClient) {
        return undefined;
      }
      const allWidgets = await conversionRpcClient.getCheckoutWidgets(
        {},
        { signal },
      );
      return selectWidgetsForExperiencePreview(
        allWidgets,
        checkoutExperienceId,
      );
    },
    [conversionRpcClient, checkoutExperienceId],
  );

  const experience = experienceLoad.value;
  const shippingMethodCount = experience?.rateTables.length ?? 0;
  const widgetCount = widgetsLoad.value?.length ?? 0;
  const upsellPageId = experience?.upsellPageId?.toString();
  const ppuCount = upsellPageId ? 1 : 0;

  const chips = useMemo<SummaryChip[]>(() => {
    const result: SummaryChip[] = [];
    if (shippingMethodCount > 0) {
      result.push({
        key: "shipping",
        Icon: TruckIcon,
        count: shippingMethodCount,
        label: t("Shipping"),
        color: "purple",
        hover: (
          <CheckoutExperienceShippingHover
            checkoutExperienceId={checkoutExperienceId}
          />
        ),
      });
    }
    if (widgetCount > 0) {
      result.push({
        key: "widgets",
        Icon: CodeBrowserIcon,
        count: widgetCount,
        label: t("Widgets"),
        color: "blue",
        hover: (
          <CheckoutExperienceWidgetsHover
            checkoutExperienceId={checkoutExperienceId}
          />
        ),
      });
    }
    if (ppuCount > 0) {
      result.push({
        key: "ppu",
        Icon: CursorClickIcon,
        count: ppuCount,
        label: t("Post-purchase"),
        color: "orange",
        hover: (
          <CheckoutExperiencePpuHover
            checkoutExperienceId={checkoutExperienceId}
          />
        ),
      });
    }
    return result;
  }, [shippingMethodCount, widgetCount, ppuCount, checkoutExperienceId, t]);

  if (chips.length === 0) {
    return null;
  }

  return (
    <Flex align="flex-start" gap="xl" wrap="wrap">
      {chips.map((chip) => (
        <Flex align="flex-start" dir="column" gap="xs" key={chip.key}>
          <Text fontSize="xxs" textColor="secondary">
            {chip.label}
          </Text>
          <Tooltip
            arrow
            className={checkoutCss.checkoutHoverCard}
            darkMode
            enterDelay={120}
            leftAlign
            paddingOverride="12px 14px"
            placement="right"
            PopperProps={{
              modifiers: [
                {
                  name: "preventOverflow",
                  options: { padding: 8, altAxis: true, tether: false },
                },
              ],
            }}
            title={chip.hover}
          >
            <div className={checkoutCss.summaryBadgeTrigger}>
              <RedoBadge
                color={chip.color}
                segmentLeading={{ type: "icon", Icon: chip.Icon }}
                size="sm"
                text={String(chip.count)}
              />
            </div>
          </Tooltip>
        </Flex>
      ))}
    </Flex>
  );
});

const MetricsCallout = memo(function MetricsCallout({
  metrics,
}: {
  metrics: Extract<Metrics, { status: "display-metrics" }>["value"];
}) {
  const { t } = useTranslation();

  return (
    <Flex
      borderRadius="md"
      className={css.metricsCalloutContainer}
      dir="column"
      gap="md"
      p="lg"
    >
      <Flex justify="space-between">
        <Text fontSize="xxs" fontWeight="regular" textColor="white">
          {t("Orders {{orderCount}}", { orderCount: metrics.orders.count })}
        </Text>
        <Text fontSize="xxs" fontWeight="regular" textColor="white">
          {t("AOV {{amount}}", {
            amount:
              metrics.orders.count > 0
                ? moneyFormat(
                    moneyMultiply(metrics.revenue, 1 / metrics.orders.count),
                  )
                : "-",
          })}
        </Text>
      </Flex>
      <Flex>
        <Text fontSize="xxs" fontWeight="regular" textColor="white">
          {t("Revenue {{amount}}", { amount: moneyFormat(metrics.revenue) })}
        </Text>
      </Flex>
    </Flex>
  );
});

type Metrics =
  | { status: "pending" }
  | { status: "metrics-not-applicable" }
  | { status: "metrics-not-available" }
  | {
      status: "display-metrics";
      value: { orders: { count: number }; revenue: Money };
    };

function useMetrics({ nodeId }: { nodeId: string }): { metrics: Metrics } {
  const { analyticsForCheckoutNodeByNodeId, checkoutTreeAnalyticsLoad } =
    useContext(CheckoutTreeAnalyticsContext);

  const metrics: Metrics = useMemo(() => {
    if (checkoutTreeAnalyticsLoad.pending && !checkoutTreeAnalyticsLoad.value) {
      return { status: "pending" };
    } else if (!checkoutTreeAnalyticsLoad.value) {
      return { status: "metrics-not-applicable" };
    } else if (!analyticsForCheckoutNodeByNodeId[nodeId]) {
      return { status: "metrics-not-available" };
    } else {
      return {
        status: "display-metrics",
        value: analyticsForCheckoutNodeByNodeId[nodeId].total,
      };
    }
  }, [analyticsForCheckoutNodeByNodeId, checkoutTreeAnalyticsLoad, nodeId]);

  return { metrics };
}
